/* eslint-disable react/no-multi-comp */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { isMobileBrowser } from '../../base/environment/utils';
import Icon from '../../base/icons/components/Icon';
import { IconGear } from '../../base/icons/svg';
import ContextMenu from '../../base/ui/components/web/ContextMenu';
/**
 * Click handler.
 *
 * @param {SyntheticEvent} event - The click event.
 * @returns {void}
 */
function onClick(event) {
    // If the event is propagated to the thumbnail container the participant will be pinned. That's why the propagation
    // needs to be stopped.
    event.stopPropagation();
}
const useStyles = makeStyles()(theme => {
    return {
        actions: {
            margin: '10px auto',
            textAlign: 'center'
        },
        assumedBandwidth: {
            cursor: 'pointer',
            margin: '0 5px'
        },
        bandwidth: {
            alignItems: 'center',
            display: 'flex'
        },
        connectionStatsTable: {
            '&, & > table': {
                fontSize: '12px',
                fontWeight: 400,
                '& td': {
                    padding: '2px 0'
                }
            },
            '& > table': {
                whiteSpace: 'nowrap'
            },
            '& td:nth-child(n-1)': {
                paddingLeft: '5px'
            },
            '& $upload, & $download': {
                marginRight: '2px'
            }
        },
        contextMenu: {
            position: 'relative',
            margin: 0,
            right: 'auto',
            padding: `${theme.spacing(2)} ${theme.spacing(1)}`
        },
        download: {},
        mobile: {
            margin: theme.spacing(3)
        },
        status: {
            fontWeight: 'bold'
        },
        upload: {},
        link: {
            cursor: 'pointer',
            color: theme.palette.text01,
            transition: 'color .2s ease',
            border: 0,
            background: 0,
            padding: 0,
            display: 'inline',
            fontWeight: 'bold',
            '&:hover': {
                color: theme.palette.link01Hover,
                textDecoration: 'underline'
            },
            '&:active': {
                color: theme.palette.link01Active
            }
        }
    };
});
const ConnectionStatsTable = ({ audioSsrc, bandwidth, bitrate, bridgeCount, codec, connectionSummary, disableShowMoreStats, e2eeVerified, enableAssumedBandwidth, enableSaveLogs, framerate, isVirtualScreenshareParticipant, isLocalVideo, isNarrowLayout, maxEnabledResolution, onOpenBandwidthDialog, onSaveLogs, onShowMore, packetLoss, participantId, region, resolution, serverRegion, shouldShowMore, transport, videoSsrc }) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const _renderResolution = () => {
        let resolutionString = 'N/A';
        if (resolution && videoSsrc) {
            const { width, height } = resolution[videoSsrc] ?? {};
            if (width && height) {
                resolutionString = `${width}x${height}`;
                if (maxEnabledResolution && maxEnabledResolution < 720 && !isVirtualScreenshareParticipant) {
                    const maxEnabledResolutionTitle = t('connectionindicator.maxEnabledResolution');
                    resolutionString += ` (${maxEnabledResolutionTitle} ${maxEnabledResolution}p)`;
                }
            }
        }
        return (React.createElement("tr", null,
            React.createElement("td", null,
                React.createElement("span", null, t('connectionindicator.resolution'))),
            React.createElement("td", null, resolutionString)));
    };
    const _renderFrameRate = () => {
        let frameRateString = 'N/A';
        if (framerate) {
            frameRateString = String(framerate[videoSsrc] ?? 'N/A');
        }
        return (React.createElement("tr", null,
            React.createElement("td", null,
                React.createElement("span", null, t('connectionindicator.framerate'))),
            React.createElement("td", null, frameRateString)));
    };
    const _renderScreenShareStatus = () => {
        const className = cx(classes.connectionStatsTable, { [classes.mobile]: isMobileBrowser() });
        return (React.createElement(ContextMenu, { className: classes.contextMenu, hidden: false, inDrawer: true },
            React.createElement("div", { className: className, onClick: onClick },
                React.createElement("tbody", null,
                    _renderResolution(),
                    _renderFrameRate()))));
    };
    const _renderBandwidth = () => {
        const { download, upload } = bandwidth || {};
        return (React.createElement("tr", null,
            React.createElement("td", null, t('connectionindicator.bandwidth')),
            React.createElement("td", { className: classes.bandwidth },
                React.createElement("span", { className: classes.download }, "\u2193"),
                download ? `${download} Kbps` : 'N/A',
                React.createElement("span", { className: classes.upload }, "\u2191"),
                upload ? `${upload} Kbps` : 'N/A',
                enableAssumedBandwidth && (React.createElement("div", { className: classes.assumedBandwidth, onClick: onOpenBandwidthDialog },
                    React.createElement(Icon, { size: 10, src: IconGear }))))));
    };
    const _renderTransportTableRow = (config) => {
        const { additionalData, data, key, label } = config;
        return (React.createElement("tr", { key: key },
            React.createElement("td", null,
                React.createElement("span", null, label)),
            React.createElement("td", null,
                getStringFromArray(data),
                additionalData || null)));
    };
    const _renderTransport = () => {
        if (!transport || transport.length === 0) {
            const NA = (React.createElement("tr", { key: 'address' },
                React.createElement("td", null,
                    React.createElement("span", null, t('connectionindicator.address'))),
                React.createElement("td", null, "N/A")));
            return [NA];
        }
        const data = {
            localIP: [],
            localPort: [],
            remoteIP: [],
            remotePort: [],
            transportType: []
        };
        for (let i = 0; i < transport.length; i++) {
            const ip = getIP(transport[i].ip);
            const localIP = getIP(transport[i].localip);
            const localPort = getPort(transport[i].localip);
            const port = getPort(transport[i].ip);
            if (!data.remoteIP.includes(ip)) {
                data.remoteIP.push(ip);
            }
            if (!data.localIP.includes(localIP)) {
                data.localIP.push(localIP);
            }
            if (!data.localPort.includes(localPort)) {
                data.localPort.push(localPort);
            }
            if (!data.remotePort.includes(port)) {
                data.remotePort.push(port);
            }
            if (!data.transportType.includes(transport[i].type)) {
                data.transportType.push(transport[i].type);
            }
        }
        // All of the transports should be either P2P or JVB
        let isP2P = false, isTURN = false;
        if (transport.length) {
            isP2P = transport[0].p2p;
            isTURN = transport[0].localCandidateType === 'relay'
                || transport[0].remoteCandidateType === 'relay';
        }
        const additionalData = [];
        if (isP2P) {
            additionalData.push(React.createElement("span", null, " (p2p)"));
        }
        if (isTURN) {
            additionalData.push(React.createElement("span", null, " (turn)"));
        }
        // First show remote statistics, then local, and then transport type.
        const tableRowConfigurations = [
            {
                additionalData,
                data: data.remoteIP,
                key: 'remoteaddress',
                label: t('connectionindicator.remoteaddress', { count: data.remoteIP.length })
            },
            {
                data: data.remotePort,
                key: 'remoteport',
                label: t('connectionindicator.remoteport', { count: transport.length })
            },
            {
                data: data.localIP,
                key: 'localaddress',
                label: t('connectionindicator.localaddress', { count: data.localIP.length })
            },
            {
                data: data.localPort,
                key: 'localport',
                label: t('connectionindicator.localport', { count: transport.length })
            },
            {
                data: data.transportType,
                key: 'transport',
                label: t('connectionindicator.transport', { count: data.transportType.length })
            }
        ];
        return tableRowConfigurations.map(_renderTransportTableRow);
    };
    const _renderRegion = () => {
        let str = serverRegion;
        if (!serverRegion) {
            return;
        }
        if (region && serverRegion && region !== serverRegion) {
            str += ` from ${region}`;
        }
        return (React.createElement("tr", null,
            React.createElement("td", null,
                React.createElement("span", null, t('connectionindicator.connectedTo'))),
            React.createElement("td", null, str)));
    };
    const _renderBridgeCount = () => {
        // 0 is valid, but undefined/null/NaN aren't.
        if (!bridgeCount && bridgeCount !== 0) {
            return;
        }
        return (React.createElement("tr", null,
            React.createElement("td", null,
                React.createElement("span", null, t('connectionindicator.bridgeCount'))),
            React.createElement("td", null, bridgeCount)));
    };
    const _renderAudioSsrc = () => (React.createElement("tr", null,
        React.createElement("td", null,
            React.createElement("span", null, t('connectionindicator.audio_ssrc'))),
        React.createElement("td", null, audioSsrc || 'N/A')));
    const _renderVideoSsrc = () => (React.createElement("tr", null,
        React.createElement("td", null,
            React.createElement("span", null, t('connectionindicator.video_ssrc'))),
        React.createElement("td", null, videoSsrc || 'N/A')));
    const _renderParticipantId = () => (React.createElement("tr", null,
        React.createElement("td", null,
            React.createElement("span", null, t('connectionindicator.participant_id'))),
        React.createElement("td", null, participantId || 'N/A')));
    const _renderE2EEVerified = () => {
        if (e2eeVerified === undefined) {
            return;
        }
        return (React.createElement("tr", null,
            React.createElement("td", null,
                React.createElement("span", null, t('connectionindicator.e2eeVerified'))),
            React.createElement("td", null, t(`connectionindicator.${e2eeVerified ? 'yes' : 'no'}`))));
    };
    const _renderAdditionalStats = () => (React.createElement("table", null,
        React.createElement("tbody", null,
            isLocalVideo ? _renderBandwidth() : null,
            isLocalVideo ? _renderTransport() : null,
            _renderRegion(),
            isLocalVideo ? _renderBridgeCount() : null,
            _renderAudioSsrc(),
            _renderVideoSsrc(),
            _renderParticipantId(),
            _renderE2EEVerified())));
    const _renderBitrate = () => {
        const { download, upload } = bitrate || {};
        return (React.createElement("tr", null,
            React.createElement("td", null,
                React.createElement("span", null, t('connectionindicator.bitrate'))),
            React.createElement("td", null,
                React.createElement("span", { className: classes.download }, "\u2193"),
                download ? `${download} Kbps` : 'N/A',
                React.createElement("span", { className: classes.upload }, "\u2191"),
                upload ? `${upload} Kbps` : 'N/A')));
    };
    const _renderCodecs = () => {
        let codecString = 'N/A';
        if (codec) {
            const audioCodec = codec[audioSsrc]?.audio;
            const videoCodec = codec[videoSsrc]?.video;
            if (audioCodec || videoCodec) {
                codecString = [audioCodec, videoCodec].filter(Boolean).join(', ');
            }
        }
        return (React.createElement("tr", null,
            React.createElement("td", null,
                React.createElement("span", null, t('connectionindicator.codecs'))),
            React.createElement("td", null, codecString)));
    };
    const _renderConnectionSummary = () => (React.createElement("tr", { className: classes.status },
        React.createElement("td", null,
            React.createElement("span", null, t('connectionindicator.status'))),
        React.createElement("td", null, connectionSummary)));
    const _renderPacketLoss = () => {
        let packetLossTableData;
        if (packetLoss) {
            const { download, upload } = packetLoss;
            packetLossTableData = (React.createElement("td", null,
                React.createElement("span", { className: classes.download }, "\u2193"),
                download === null ? 'N/A' : `${download}%`,
                React.createElement("span", { className: classes.upload }, "\u2191"),
                upload === null ? 'N/A' : `${upload}%`));
        }
        else {
            packetLossTableData = React.createElement("td", null, "N/A");
        }
        return (React.createElement("tr", null,
            React.createElement("td", null,
                React.createElement("span", null, t('connectionindicator.packetloss'))),
            packetLossTableData));
    };
    const _renderSaveLogs = () => (React.createElement("span", null,
        React.createElement("button", { className: cx(classes.link, 'savelogs'), onClick: onSaveLogs, type: 'button' }, t('connectionindicator.savelogs')),
        React.createElement("span", null, " | ")));
    const _renderShowMoreLink = () => {
        const translationKey = shouldShowMore
            ? 'connectionindicator.less'
            : 'connectionindicator.more';
        return (React.createElement("button", { className: cx(classes.link, 'showmore'), onClick: onShowMore, type: 'button' }, t(translationKey)));
    };
    const _renderStatistics = () => (React.createElement("table", null,
        React.createElement("tbody", null,
            _renderConnectionSummary(),
            _renderBitrate(),
            _renderPacketLoss(),
            _renderResolution(),
            _renderFrameRate(),
            _renderCodecs())));
    if (isVirtualScreenshareParticipant) {
        return _renderScreenShareStatus();
    }
    return (React.createElement(ContextMenu, { className: classes.contextMenu, hidden: false, inDrawer: true },
        React.createElement("div", { className: cx(classes.connectionStatsTable, {
                [classes.mobile]: isMobileBrowser() || isNarrowLayout
            }), onClick: onClick },
            _renderStatistics(),
            React.createElement("div", { className: classes.actions },
                isLocalVideo && enableSaveLogs ? _renderSaveLogs() : null,
                !disableShowMoreStats && _renderShowMoreLink()),
            shouldShowMore ? _renderAdditionalStats() : null)));
};
/**
 * Utility for getting the IP from a transport statistics object's
 * representation of an IP.
 *
 * @param {string} value - The transport's IP to parse.
 * @private
 * @returns {string}
 */
function getIP(value) {
    if (!value) {
        return '';
    }
    return value.substring(0, value.lastIndexOf(':'));
}
/**
 * Utility for getting the port from a transport statistics object's
 * representation of an IP.
 *
 * @param {string} value - The transport's IP to parse.
 * @private
 * @returns {string}
 */
function getPort(value) {
    if (!value) {
        return '';
    }
    return value.substring(value.lastIndexOf(':') + 1, value.length);
}
/**
 * Utility for concatenating values in an array into a comma separated string.
 *
 * @param {Array} array - Transport statistics to concatenate.
 * @private
 * @returns {string}
 */
function getStringFromArray(array) {
    let res = '';
    for (let i = 0; i < array.length; i++) {
        res += (i === 0 ? '' : ', ') + array[i];
    }
    return res;
}
export default ConnectionStatsTable;
